import { Controller } from "@hotwired/stimulus"

// Spinner SVG matches the pattern used by autoboost_card_controller.js.
const SPINNER_SVG = `<svg class="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
  <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
  <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
</svg>`

// Attach to a <form> to prevent the double-submit double-action bug
// (incident 2026-05-27: double-clicked Retry on FbAd 2216 created two
// real Meta ads via two queued AdCreationJobs whose lock_keys didn't
// collide). On submit, disable the submit button and swap its label /
// icon to a spinner. Restore on `turbo:submit-end` so the UI recovers
// if the form errors and stays on-page.
//
// Usage:
//   <form data-controller="disable-on-submit"
//         data-disable-on-submit-text-value="Retrying...">
//     <button type="submit">Retry</button>
//   </form>
//
// Omit `text-value` for icon-only buttons (e.g. the dashboard Retry
// icon) — the controller will swap the existing <svg> with a spinner
// in place.
export default class extends Controller {
  static values = { text: String }

  connect() {
    this._onSubmit = this.disable.bind(this)
    this._onEnd   = this.restore.bind(this)
    this.element.addEventListener("submit", this._onSubmit)
    this.element.addEventListener("turbo:submit-end", this._onEnd)
  }

  disconnect() {
    this.element.removeEventListener("submit", this._onSubmit)
    this.element.removeEventListener("turbo:submit-end", this._onEnd)
  }

  disable(event) {
    // Disable the button that was actually clicked (event.submitter), so a
    // multi-submit form (e.g. the wizard's Save-draft / Next / Finalize)
    // only spins the pressed button — not the first one in the DOM. Falls
    // back to the first submit button for single-button forms (Retry).
    const btn = event?.submitter || this._submitButton()
    if (!btn || btn.disabled) return
    this._activeBtn = btn

    this._originalHTML = btn.innerHTML
    btn.disabled = true
    btn.classList.add("opacity-50", "pointer-events-none")

    if (this.textValue) {
      btn.innerHTML = SPINNER_SVG + `<span>${this.textValue}</span>`
      btn.classList.add("inline-flex", "items-center", "gap-2")
    } else {
      const svg = btn.querySelector("svg")
      if (svg) svg.outerHTML = SPINNER_SVG
    }
  }

  restore(event) {
    // Successful submits typically redirect away — restoring the button
    // before navigation would briefly flash the original state. Only
    // restore on failure.
    if (event?.detail?.success) return

    const btn = this._activeBtn || this._submitButton()
    if (!btn || this._originalHTML == null) return

    btn.disabled = false
    btn.classList.remove("opacity-50", "pointer-events-none")
    btn.innerHTML = this._originalHTML
  }

  _submitButton() {
    return this.element.querySelector('button[type="submit"], button:not([type])')
  }
}
